/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { MaskedIntegerField } from './MaskedNumberField';
import type { PropertyFormValues } from './schema';

interface AddressUnitFieldsProps {
  form: UseFormReturn<PropertyFormValues>;
}

type UnitKey = 'block' | 'unit' | 'quadra' | 'lot';

/** Block, unit (apto), quadra and lot: short text, in the order brokers say them. */
const UNIT_FIELDS: ReadonlyArray<readonly [UnitKey, string]> = [
  ['block', 'propertyDrawer.address.block'],
  ['unit', 'propertyDrawer.address.unit'],
  ['quadra', 'propertyDrawer.address.quadra'],
  ['lot', 'propertyDrawer.address.lot'],
];

const UNIT_MAX = 30;

/**
 * The distance to the sea, in whole metres.
 *
 * @param props - The property drawer form.
 * @returns The distance field.
 */
export function DistanceToSeaField({ form }: AddressUnitFieldsProps) {
  const { t } = useTranslation('features/properties');
  return (
    <Controller
      name="distance_to_sea_m"
      control={form.control}
      render={({ field }) => (
        <MaskedIntegerField
          label={t('propertyDrawer.address.distanceToSea')}
          suffix="m"
          maxDigits={6}
          error={form.formState.errors.distance_to_sea_m?.message}
          value={field.value}
          onChange={field.onChange}
        />
      )}
    />
  );
}

/**
 * Block, unit, quadra and lot of the listing, in one row of four on desktop.
 *
 * @param props - The property drawer form.
 * @returns The unit fields.
 */
export function AddressUnitFields({ form }: AddressUnitFieldsProps) {
  const { t } = useTranslation('features/properties');
  return (
    <div className="grid grid-cols-2 gap-(--space-3) sm:grid-cols-4" data-testid="address-unit-fields">
      {UNIT_FIELDS.map(([name, labelKey]) => (
        <Controller
          key={name}
          name={name}
          control={form.control}
          render={({ field }) => (
            <TextInput
              label={t(labelKey)}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              maxLength={UNIT_MAX}
            />
          )}
        />
      ))}
    </div>
  );
}
